import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { WkoLoadingSpinner } from "../WkoLoadingSpinner";
import { notifyError } from "../../utils/notifications";
import { BenchmarkCard } from "./BenchmarkCard";
const ACTIVITY_TYPE_COLOURS = {
    routine: "#E83052",
    overriding: "#5E90C3",
};
const LEVELS = [
    { key: "NcLevel1Value", routineKey: "NcLevel1RoutineTotal", overridingKey: "NcLevel1OverridingTotal", label: "L1" },
    { key: "NcLevel2Value", routineKey: "NcLevel2RoutineTotal", overridingKey: "NcLevel2OverridingTotal", label: "L2" },
    { key: "NcLevel3Value", routineKey: "NcLevel3RoutineTotal", overridingKey: "NcLevel3OverridingTotal", label: "L3" },
    { key: "NcLevel4Value", routineKey: "NcLevel4RoutineTotal", overridingKey: "NcLevel4OverridingTotal", label: "L4" },
];
export function LevelsActivitiesPanel({ options, selectedActivityType, title = "Activities causing non-compliance", }) {
    const [loading, setLoading] = useState(true);
    const [levelData, setLevelData] = useState([]);
    const [hoveredActivity, setHoveredActivity] = useState(null);
    const chartRefs = [
        useRef(null),
        useRef(null),
        useRef(null),
        useRef(null),
    ];
    const enableClick = selectedActivityType !== undefined;
    const activityTypeRef = useRef(options.activityType);
    activityTypeRef.current = options.activityType;
    useEffect(() => {
        if (!options.isReady)
            return;
        setLevelData([]);
        setLoading(true);
        const params = new URLSearchParams({
            fromMonth: options.startDate.substring(0, 7),
            toMonth: options.endDate.substring(0, 7),
            fleets: options.fleet.map((f) => f.ID).join(","),
            locationId: options.locationId == null ? "" : String(options.locationId),
        });
        fetch(`/api/levelsbenchmark/nclevelactivities?${params}`)
            .then((res) => {
            if (res.ok)
                return res.json();
            throw new Error(res.statusText);
        })
            .then((stats) => {
            const data = LEVELS.map((level) => {
                const total = stats.StatsByActivity.reduce((sum, x) => sum + x[level.key], 0);
                const activities = stats.StatsByActivity.map((s) => ({
                    name: s.Name,
                    y: total > 0 ? parseFloat(((s[level.key] / total) * 100).toFixed(2)) : 0,
                    sliced: s.Name === options.activityType,
                }));
                const routineTotal = stats[level.routineKey];
                const overridingTotal = stats[level.overridingKey];
                const typesTotal = routineTotal + overridingTotal;
                const activityTypes = [
                    { name: "Routine operations", y: typesTotal > 0 ? parseFloat(((routineTotal / typesTotal) * 100).toFixed(2)) : 0, color: ACTIVITY_TYPE_COLOURS.routine },
                    { name: "Overriding operations", y: typesTotal > 0 ? parseFloat(((overridingTotal / typesTotal) * 100).toFixed(2)) : 0, color: ACTIVITY_TYPE_COLOURS.overriding },
                ];
                return { activities, activityTypes };
            });
            setLevelData(data);
        })
            .catch(() => {
            notifyError("An error has occurred when loading NC level activity stats. Please try again.");
        })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        options.isReady,
        options.startDate,
        options.endDate,
        options.fleet.map((f) => f.ID).join(","),
        options.locationId,
        options.activityType,
    ]);
    const handleClick = (name) => {
        if (enableClick)
            selectedActivityType(name);
    };
    const makeChartOptions = useMemo(() => (label) => ({
        credits: { enabled: false },
        chart: { type: "pie", styledMode: false, backgroundColor: "transparent" },
        exporting: { enabled: false },
        title: { text: undefined },
        subtitle: { text: label, floating: true, verticalAlign: "middle", style: { fontSize: "1rem" } },
        plotOptions: {
            pie: {
                allowPointSelect: enableClick,
                cursor: enableClick ? "pointer" : "default",
                states: { inactive: { opacity: 0.2 } },
                dataLabels: {
                    distance: -50,
                    enabled: true,
                    formatter: function () {
                        if (this.point.options.sliced) {
                            return `<b>${this.point.name}</b><br/><b>${this.point.y}%</b>`;
                        }
                        return undefined;
                    },
                },
                point: {
                    events: {
                        click: function () {
                            if (this.series.name === "Activities")
                                handleClick(this.name);
                        },
                        mouseOver: function () {
                            setHoveredActivity(this.name);
                        },
                        mouseOut: function () {
                            setHoveredActivity(null);
                        },
                    },
                },
            },
        },
        tooltip: { pointFormat: "<b>{point.y}%</b>" },
    }), [enableClick]);
    const levelChartOptions = useMemo(() => LEVELS.map((level, i) => ({
        ...makeChartOptions(level.label),
        series: levelData[i]
            ? [
                { type: "pie", colorByPoint: true, data: levelData[i].activities, name: "Activities", size: "100%", innerSize: "60%" },
                { type: "pie", colorByPoint: false, color: "gray", data: levelData[i].activityTypes, name: "Activity Types", size: "60%", innerSize: "80%", allowPointSelect: false },
            ]
            : [],
    })), [makeChartOptions, levelData]);
    useEffect(() => {
        if (!hoveredActivity)
            return;
        chartRefs.forEach((ref) => {
            const chart = ref.current?.chart;
            if (!chart)
                return;
            let point = chart.series[0]?.points?.find((p) => p.name === hoveredActivity);
            if (point) {
                if (enableClick && point.name !== activityTypeRef.current)
                    point.select(true, false);
            }
            else {
                point = chart.series[1]?.points?.find((p) => p.name === hoveredActivity);
            }
            if (point && point.name !== activityTypeRef.current)
                chart.tooltip.refresh(point);
        });
        return () => {
            chartRefs.forEach((ref) => {
                const chart = ref.current?.chart;
                if (!chart)
                    return;
                chart.series[0]?.points?.forEach((p) => {
                    if (enableClick && p.name !== activityTypeRef.current)
                        p.select(false, false);
                });
                chart.tooltip.hide();
            });
        };
    }, [hoveredActivity, enableClick]);
    if (loading) {
        return (_jsx(BenchmarkCard, { title: title, children: _jsx(WkoLoadingSpinner, {}) }));
    }
    const legendActivities = levelData.length > 0 ? levelData[0].activities : [];
    const legendTypes = levelData.length > 0 ? levelData[0].activityTypes : [];
    if (legendActivities.length === 0) {
        return (_jsx(BenchmarkCard, { title: title, children: _jsx("div", { className: "py-5", children: _jsx("p", { className: "text-secondary text-center mb-0", children: "No data for this selection." }) }) }));
    }
    return (_jsx(BenchmarkCard, { title: title, children: _jsxs("div", { className: "w-100", children: [_jsx("div", { className: "row activity-stats justify-content-around", children: LEVELS.map((level, i) => (_jsx("div", { className: "col-xl-3 col-lg-6 col-md-6 col-sm-6", children: _jsx("div", { className: "soft-light-overlay", children: _jsx(HighchartsReact, { highcharts: Highcharts, ref: chartRefs[i], options: levelChartOptions[i] }) }) }, level.label))) }), _jsxs("div", { className: "w-100 activity-stats", children: [_jsx("div", { className: "mb-3", children: legendActivities.map((a, i) => (_jsxs("div", { className: "me-4 mb-2 d-inline-block", style: { cursor: enableClick ? "pointer" : "default" }, onClick: () => handleClick(a.name), onMouseEnter: () => setHoveredActivity(a.name), onMouseLeave: () => setHoveredActivity(null), children: [_jsx("div", { className: `symbol d-inline-block align-middle highcharts-color-bg${i}`, style: { height: "1.5rem", width: "1.5rem" }, children: "\u00A0" }), _jsx("span", { className: "ps-2", children: a.name })] }, a.name))) }), _jsx("div", { children: legendTypes.map((a) => (_jsxs("div", { className: "me-4 mb-2 d-inline-block", children: [_jsx("span", { className: "symbol d-inline-block align-middle", style: { height: "0.5rem", width: "5rem", backgroundColor: a.color } }), _jsx("span", { className: "ps-2", children: a.name })] }, a.name))) })] })] }) }));
}
